import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import DetailedTable from '@repo/ui/detailed-table'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { getRecommendedAllocations } from '../../../api/trip-dates/get-recommended-allocations'
import { getTripDate, getTripPlaceToCenterRequests } from '../../../api/trip-dates/get-trip-date'
import { tripStepTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import { AllocatedPlaceInput } from '../../../lib/types/models/trip-booking/trip-date-input'
import { TripPlaceToCenterRequest } from '../../../lib/types/models/trip-place-to-center-request/trip-place-to-center-request'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import { ItemNotFound } from '../../elements/item-not-found'
import LoadingScreen from '../../elements/loading/loading-screen'
import ValidationButtons from '../../elements/validation-buttons'
import { TripBookingDetailsBadge } from '../trip-bookings/trip-booking.ui'
import { getRemainingPlaces } from './trip-date.utils'
import { tripDateBookingToCenterFormSchema } from './trip-dates.forms'
import { NumberOfPlacesBadge } from './trip-dates.ui'

type Props = BaseFormProps<AllocatedPlaceFormInput> & {
  tripDateId: number
}

export type AllocatedPlaceFormInput = {
  data: AllocatedPlaceInput[]
}

export const TripDateBookingToCenterForm = ({ tripDateId, ...validationButtonProps }: Props) => {
  const { data: tripDate, isPending } = useQuery({
    queryKey: ['trip-date', tripDateId],
    queryFn: () => getTripDate(tripDateId)
  })

  const { data: tripPlaceToCenterRequests } = useQuery({
    queryKey: ['trip-place-to-center-requests', tripDateId],
    queryFn: async () => {
      const res = await getTripPlaceToCenterRequests(tripDateId)

      const data = TripDateProperties.sortTripPlaceToCenterRequestsAllocatedPlaces(res.data)
      return { ...res, data }
    }
  })

  const { data: recommendedAllocations } = useQuery({
    queryKey: ['recommended-allocations', tripDateId],
    queryFn: () => getRecommendedAllocations(tripDateId),
    enabled: !!tripPlaceToCenterRequests?.data
  })

  const form = useForm<AllocatedPlaceFormInput>({
    resolver: zodResolver(tripDateBookingToCenterFormSchema(tripPlaceToCenterRequests?.data)),
    defaultValues: { data: [] },
    mode: FORM_MODE
  })

  const { data } = form.watch()
  const remainingPlaces = getRemainingPlaces(data, tripPlaceToCenterRequests?.data ?? [])

  useEffect(() => {
    if (!tripPlaceToCenterRequests) return
    const allocationPlaces = tripPlaceToCenterRequests.data.flatMap((t) => t.allocatedPlaces)
    const hasInputedData = allocationPlaces.some(
      (ap) => ap.numberOfStudentsPlacesAllocated > 0 || ap.numberOfStaffPlacesAllocated > 0
    )

    let newData: AllocatedPlaceInput[] = []

    if (hasInputedData) {
      newData = allocationPlaces.map((allocatedPlace) => ({
        allocatedPlaceId: allocatedPlace.id,
        numberOfStudentsPlacesAllocated: allocatedPlace.numberOfStudentsPlacesAllocated,
        numberOfStaffPlacesAllocated: allocatedPlace.numberOfStaffPlacesAllocated
      }))
    } else if (recommendedAllocations) {
      newData = allocationPlaces.map(
        (allocatedPlace) =>
          recommendedAllocations.find((r) => r.allocatedPlaceId === allocatedPlace.id) ?? {
            allocatedPlaceId: allocatedPlace.id,
            numberOfStudentsPlacesAllocated: 0,
            numberOfStaffPlacesAllocated: 0
          }
      )
    }

    form.setValue('data', newData)
  }, [tripPlaceToCenterRequests, recommendedAllocations])

  if (!data || isPending) return <LoadingScreen />
  if (!tripDate) return <ItemNotFound title={'Date de voyage non trouvée'} />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'p-4 flex-col flex gap-3'}>
          <div className="flex flex-wrap gap-2 items-center bg-white-hover rounded-lg border border-grey-light px-3 py-2 w-fit">
            <Typography variant={'body-medium'}>
              {`${TripProperties.getTripLabel(tripDate.trip)} -`}
            </Typography>
            <Typography
              variant={'body-medium'}
              color={
                tripDate.directionality === TripStepTypeEnum.INBOUND
                  ? tailwindColors.secondary.DEFAULT
                  : tailwindColors.green.DEFAULT
              }
            >
              {tripStepTypeMapping[tripDate.directionality].label}
            </Typography>
            <Badge
              text={DateFormatter.DDMMYYYY(tripDate.date)}
              variant={tripStepTypeMapping[tripDate.directionality].variant}
            />
          </div>

          <DetailedTable<TripPlaceToCenterRequest>
            groupBy={'centerId'}
            data={
              tripPlaceToCenterRequests?.data.sort((a, b) =>
                a.center.name.localeCompare(b.center.name)
              ) ?? []
            }
            columns={[
              {
                key: 'centerId',
                header: 'Centre',
                render: ({ center, departureDate, arrivalDate }) => (
                  <div className={'flex flex-col gap-2 w-40'}>
                    <Typography variant={'secondary-body'}>{center.name}</Typography>
                    <Badge
                      size="medium"
                      variant={'grey'}
                      text={
                        <div className="flex flex-col">
                          <span>{DateFormatter.DDMMYYYY(departureDate)}</span>
                          <span>{DateFormatter.DDMMYYYY(arrivalDate)}</span>
                        </div>
                      }
                    />
                  </div>
                )
              },
              {
                key: 'numberOfStaffPlacesRequested',
                header: '# Places demandées',
                borderRight: true,
                render: (data) => (
                  <NumberOfPlacesBadge
                    className={' py-2'}
                    studentsPlaces={data.numberOfStudentsPlacesRequested}
                    staffPlaces={data.numberOfStaffPlacesRequested}
                  />
                )
              },
              {
                key: 'allocatedPlaces',
                header: (
                  <div className="flex items-center justify-between px-2">
                    <span>Réservations</span>
                    <span className="pr-20"># Places réparties</span>
                  </div>
                ),
                render: (tripPlaceToCenterRequest) => {
                  return (
                    <div className="flex flex-col justify-between items-center gap-5 py-2">
                      {tripPlaceToCenterRequest.allocatedPlaces.map((allocatedPlace) => {
                        // get the index of the trip booking in the form data
                        const index = data.findIndex(
                          (tb) => tb.allocatedPlaceId === allocatedPlace.id
                        )
                        const item = data[index]

                        return (
                          <div
                            className={'flex justify-center items-center w-full gap-8'}
                            key={allocatedPlace.id}
                          >
                            <TripBookingDetailsBadge
                              directionality={tripDate.directionality}
                              key={allocatedPlace.tripBooking.id}
                              tripBooking={allocatedPlace.tripBooking}
                              detailOptions={{ count: ['reserved-allocated'] }}
                            />

                            <div className={'flex justify-end items-center w-full gap-2'}>
                              <FormField
                                control={form.control}
                                name={`data.${index}.numberOfStudentsPlacesAllocated`}
                                render={({ field }) => (
                                  <FormItem className="w-36">
                                    <FormControl>
                                      <ControlledInput
                                        {...field}
                                        required
                                        labelClassName="w-fit"
                                        type={'number'}
                                        value={item?.numberOfStudentsPlacesAllocated ?? ''}
                                        label={'# jeunes'}
                                        onChange={(value) => {
                                          field.onChange(value ? +value : value)
                                          form.trigger()
                                        }}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name={`data.${index}.numberOfStaffPlacesAllocated`}
                                render={({ field }) => (
                                  <FormItem className="w-36">
                                    <FormControl>
                                      <ControlledInput
                                        {...field}
                                        labelClassName="w-fit"
                                        value={item?.numberOfStaffPlacesAllocated ?? ''}
                                        type={'number'}
                                        label={'# encadrants'}
                                        onChange={(value) => {
                                          field.onChange(value ? +value : value)
                                          form.trigger()
                                        }}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                }
              }
            ]}
            headerClassName={'bg-white-hover'}
            containerClassName={'border-grey-light'}
            displayGroupByBorder={false}
          />

          <div className={'flex flex-wrap gap-2'}>
            <Typography
              variant={'label-medium'}
              color={tailwindColors.fushia.DEFAULT}
              className={'bg-white-hover px-3 py-2 w-fit rounded'}
            >
              {`Nombre total de places jeunes restantes à répartir : ${remainingPlaces.remainingStudentPlaces}`}
            </Typography>
            <Typography
              variant={'label-medium'}
              color={tailwindColors.blue.DEFAULT}
              className={'bg-white-hover px-3 py-2 w-fit rounded'}
            >
              {`Nombre total de places encadrants restantes à répartir : ${remainingPlaces.remainingStaffPlaces}`}
            </Typography>
          </div>
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
